import { ProwlRenderer } from '@aplicy-com/prowl-components';
import type {
  ProwlComponentEvent,
  ProwlMessage,
} from '../../../../../lib/prowl/useProwlConversation';

interface MessageBubbleV2Props {
  message: ProwlMessage;
  onAction: (actionKey: string, payload: unknown) => void;
  onInteract?: (event: string, payload: unknown) => void;
}

// prowl-components renders ServiceCardList's own "no results" text even when
// the model's message already explains that nothing was found — that reads
// as a duplicated, visible empty block. Drop it here instead of showing it.
const isEmptyServiceCardList = (c: ProwlComponentEvent): boolean =>
  c.type === 'ServiceCardList' &&
  Array.isArray((c.props as { services?: unknown[] })?.services) &&
  (c.props as { services: unknown[] }).services.length === 0;

const SERVICE_CARD_LIST_CAP = 3;

/**
 * design-spec 1j: off the results route (the only place ServiceCardList is
 * never used — it renders result_set-driven chips/grid there instead), the
 * floating widget keeps full inline cards, capped at 3 with a "see all"
 * NavigateChip appended as its own synthetic component when the model
 * returned more than that.
 */
const capServiceCardList = (c: ProwlComponentEvent): ProwlComponentEvent[] => {
  if (c.type !== 'ServiceCardList') return [c];
  const services = (c.props as { services?: unknown[] })?.services;
  if (!Array.isArray(services) || services.length <= SERVICE_CARD_LIST_CAP) {
    return [c];
  }
  const capped: ProwlComponentEvent = {
    ...c,
    props: {
      ...(c.props as object),
      services: services.slice(0, SERVICE_CARD_LIST_CAP),
    },
  };
  const seeAllChip: ProwlComponentEvent = {
    componentId: `${c.componentId}-see-all`,
    type: 'NavigateChip',
    props: {
      label: `See all ${services.length} results`,
      routeKey: 'servicesSearch',
    },
  };
  return [capped, seeAllChip];
};

const TOOL_STATUS_ICON: Record<string, string> = {
  running: '●', // filled dot — active
  ok: '✓',
  error: '✕',
  timeout: '⏱',
};

/**
 * tool_started / tool_finished rendered as visible, labelled activity —
 * never a bare spinner (task requirement 3).
 */
const ToolActivityRow = ({
  label,
  status,
}: {
  label: string;
  status: string;
}) => (
  <div className="flex items-center gap-1.5 text-[12px] text-gray-500 px-1">
    <span
      className={
        status === 'running'
          ? 'animate-pulse text-gray-400'
          : status === 'ok'
            ? 'text-green-600'
            : status === 'timeout'
              ? 'text-amber-600'
              : 'text-red-500'
      }
    >
      {TOOL_STATUS_ICON[status] ?? TOOL_STATUS_ICON.running}
    </span>
    <span>{label}</span>
  </div>
);

const MessageBubbleV2 = ({
  message,
  onAction,
  onInteract,
}: MessageBubbleV2Props) => {
  if (message.role === 'user') {
    return (
      <div className="flex flex-col items-end gap-2">
        <div
          className="max-w-[78%] text-white px-[15px] py-[9px] text-[14px] leading-[1.45]"
          style={{
            background: 'var(--chat-accent, #317690)',
            borderRadius: '16px 16px 4px 16px',
          }}
        >
          {message.text}
        </div>
      </div>
    );
  }

  const showCursor = message.streaming;

  return (
    <div className="flex flex-col items-start gap-2 w-full">
      {(message.text || message.streaming) && (
        <div
          className="max-w-[84%] bg-white border border-gray-200 text-gray-700 px-[15px] py-3 text-[14px] leading-[1.55] shadow-sm"
          style={{ borderRadius: '16px 16px 16px 4px' }}
        >
          {message.text}
          {showCursor && (
            <span
              className="inline-block w-[2px] h-[13px] ml-0.5 align-middle"
              style={{
                background: 'var(--chat-accent, #317690)',
                animation: 'hp-blink 1s step-start infinite',
              }}
            />
          )}
        </div>
      )}

      {message.activity.length > 0 && (
        <div className="flex flex-col gap-1 max-w-[84%]">
          {message.activity.map((a) => (
            <ToolActivityRow
              key={a.toolCallId}
              label={a.label}
              status={a.status}
            />
          ))}
        </div>
      )}

      {message.components
        .filter((c) => !isEmptyServiceCardList(c))
        .flatMap(capServiceCardList)
        .map((c) => (
          <div key={c.componentId} className="max-w-[92%] w-full">
            <ProwlRenderer
              type={c.type}
              props={c.props}
              onAction={c.actionKey ? onAction : undefined}
              onInteract={onInteract}
            />
          </div>
        ))}

      {message.error && (
        <div className="max-w-[84%] text-[12.5px] text-red-600 px-1">
          {message.error.message}
        </div>
      )}
    </div>
  );
};

export default MessageBubbleV2;
